import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Paper, Typography, Stack, Button, TextField, MenuItem, Grid, Alert,
  Autocomplete, Chip, Divider, CircularProgress, LinearProgress,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Save as SaveIcon,
  Edit as EditIcon,
  Business as BusinessIcon,
  LocationOn as LocationIcon,
  Category as CategoryIcon,
  AutoAwesome as SuggestIcon,
} from '@mui/icons-material';
import {
  getLead, createLead, updateLead,
  getIndustries, createIndustry,
  getUsers, getProfilingTemplates,
} from '../services/api';

import { LEAD_STATUS_OPTIONS } from '../constants/leadStatus';
const STATUSES = LEAD_STATUS_OPTIONS.map(o => o.value);
const SOURCES = ['ManualEntry', 'CsvImport', 'Google', 'LinkedIn', 'CrunchBase', 'Owler', 'Referral', 'Other', 'AiSearch'];
const COMPANY_TYPES = ['Private', 'Public', 'Nonprofit', 'Government', 'Subsidiary', 'Educational', 'Self-Employed', 'Partnership'];

const SECTIONS = [
  {
    key: 'identity',
    label: 'Company Identity',
    description: 'Who they are and what they do',
    icon: <BusinessIcon />,
    fields: ['companyName', 'website', 'linkedInUrl', 'companyType', 'headcount', 'description', 'industryId'],
  },
  {
    key: 'location',
    label: 'Location & Contact',
    description: 'Where to reach the company',
    icon: <LocationIcon />,
    fields: ['address', 'city', 'state', 'postalCode', 'country', 'countryCode', 'phone', 'email'],
  },
  {
    key: 'classification',
    label: 'Classification',
    description: 'Source, status and ownership',
    icon: <CategoryIcon />,
    fields: ['source', 'sourceDetail', 'tags', 'status', 'assignedToUserId', 'profilingTemplateId', 'notes'],
  },
];

const FIELD_LABELS = {
  companyName: 'Company Name',
  website: 'Domain / Website',
  linkedInUrl: 'LinkedIn URL',
  companyType: 'Company Type',
  headcount: 'Headcount',
  description: 'Description',
  industryId: 'Industry',
  address: 'Street Address',
  city: 'City',
  state: 'State / Region',
  postalCode: 'Postal Code',
  country: 'Country',
  countryCode: 'Country Code',
  phone: 'Phone',
  email: 'Email',
  source: 'Source',
  sourceDetail: 'Source Detail',
  tags: 'Tags',
  status: 'Status',
  assignedToUserId: 'Assigned To',
  profilingTemplateId: 'Profiling Template',
  notes: 'Internal Notes',
};

const SUGGEST_HINTS = {
  description: 'Short summary of what the company does — drives ICP matching and personalization.',
  industryId: 'Pick an industry so segmentation and scoring work correctly.',
  headcount: 'Approximate employees — used by ICP scoring.',
  companyType: 'Private / Public / Nonprofit etc.',
  website: 'Primary domain — used for enrichment.',
  linkedInUrl: 'Company LinkedIn — speeds up contact discovery.',
  city: 'City improves geo-targeting.',
  state: 'State / region improves geo-targeting.',
  postalCode: 'Postal code helps territory routing.',
  country: 'Country — required for many enrichment providers.',
  phone: 'Office phone — useful for outreach.',
  email: 'Generic / info email if no contact yet.',
  tags: 'Comma-separated labels for filtering.',
};

const empty = {
  companyName: '', website: '', linkedInUrl: '', companyType: '', headcount: '',
  description: '', industryId: '', address: '', city: '', state: '', postalCode: '',
  country: '', countryCode: '', phone: '', email: '', source: 'ManualEntry',
  sourceDetail: '', tags: '', status: 'New',
  assignedToUserId: '', profilingTemplateId: '', notes: '',
};

export default function LeadWizardPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [form, setForm] = useState(empty);
  const [industries, setIndustries] = useState([]);
  const [users, setUsers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [addingIndustry, setAddingIndustry] = useState(false);

  const sectionRefs = useRef({});
  const fieldRefs = useRef({});

  useEffect(() => {
    getIndustries().then(r => setIndustries(r.data || [])).catch(() => {});
    getUsers().then(r => setUsers(r.data || [])).catch(() => {});
    getProfilingTemplates().then(r => setTemplates(r.data || [])).catch(() => {});
  }, []);

  const fetchLead = useCallback(async () => {
    if (!isEdit) return;
    try {
      setLoading(true);
      const { data } = await getLead(id);
      setForm({
        companyName: data.companyName || '',
        website: data.website || '',
        linkedInUrl: data.linkedInUrl || '',
        companyType: data.companyType || '',
        headcount: data.headcount ?? '',
        description: data.description || '',
        industryId: data.industryId || '',
        address: data.address || '',
        city: data.city || '',
        state: data.state || '',
        postalCode: data.postalCode || '',
        country: data.country || '',
        countryCode: data.countryCode || '',
        phone: data.phone || '',
        email: data.email || '',
        source: data.source || 'ManualEntry',
        sourceDetail: data.sourceDetail || '',
        tags: data.tags || '',
        status: data.status || 'New',
        assignedToUserId: data.assignedToUserId || '',
        profilingTemplateId: data.profilingTemplateId || '',
        notes: data.notes || '',
      });
    } catch {
      setError('Failed to load lead');
    } finally {
      setLoading(false);
    }
  }, [id, isEdit]);

  useEffect(() => { fetchLead(); }, [fetchLead]);

  const setField = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const completeness = useMemo(() => {
    const tracked = ['companyName', 'website', 'linkedInUrl', 'companyType', 'headcount',
      'description', 'industryId', 'city', 'state', 'postalCode', 'country',
      'phone', 'email', 'tags'];
    const filled = tracked.filter(k => {
      const v = form[k];
      return v !== '' && v !== null && v !== undefined;
    }).length;
    return Math.round((filled / tracked.length) * 100);
  }, [form]);

  const missingFields = useMemo(() => {
    const tracked = ['description', 'industryId', 'companyType', 'headcount',
      'website', 'linkedInUrl', 'city', 'state', 'postalCode', 'country',
      'phone', 'email', 'tags'];
    return tracked.filter(k => {
      const v = form[k];
      return v === '' || v === null || v === undefined;
    });
  }, [form]);

  const handleAddIndustry = async (name) => {
    const trimmed = (name || '').trim();
    if (!trimmed) return;
    setAddingIndustry(true);
    try {
      const { data } = await createIndustry({ name: trimmed });
      setIndustries(prev => [...prev, data]);
      setField('industryId', data.id);
    } catch (e) {
      setError(e.response?.data?.message || `Failed to add industry "${trimmed}"`);
    } finally {
      setAddingIndustry(false);
    }
  };

  const buildPayload = () => {
    const p = { ...form };
    p.industryId = p.industryId === '' ? null : p.industryId;
    p.headcount = p.headcount === '' ? null : Number(p.headcount);
    p.assignedToUserId = p.assignedToUserId === '' ? null : p.assignedToUserId;
    p.profilingTemplateId = p.profilingTemplateId === '' ? null : p.profilingTemplateId;
    Object.keys(p).forEach(k => { if (p[k] === '') p[k] = null; });
    return p;
  };

  const handleSave = async () => {
    setError('');
    if (!form.companyName || !form.companyName.trim()) {
      setError('Company Name is required');
      jumpToField('companyName');
      return;
    }
    setSaving(true);
    try {
      const payload = buildPayload();
      let leadId;
      if (isEdit) {
        await updateLead(id, payload);
        leadId = id;
      } else {
        const { data } = await createLead(payload);
        leadId = data.id;
      }
      navigate(`/leads/${leadId}`);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to save lead');
    } finally {
      setSaving(false);
    }
  };

  const jumpToField = (key) => {
    const el = fieldRefs.current[key];
    if (el && el.scrollIntoView) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const input = el.querySelector('input, textarea');
      if (input) setTimeout(() => input.focus(), 350);
      return;
    }
    const sect = SECTIONS.find(s => s.fields.includes(key));
    if (sect && sectionRefs.current[sect.key]) {
      sectionRefs.current[sect.key].scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const fieldBox = (key, node) => (
    <Box ref={el => { if (el) fieldRefs.current[key] = el; }}>{node}</Box>
  );

  const sectionHeader = (s) => (
    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2.5 }}>
      <Box sx={{ width: 36, height: 36, borderRadius: 1.5, bgcolor: '#eef2ff', color: '#4f46e5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {s.icon}
      </Box>
      <Box>
        <Typography sx={{ fontWeight: 700, fontSize: '1.0625rem', color: '#0f172a' }}>{s.label}</Typography>
        <Typography sx={{ fontSize: '0.8125rem', color: '#64748b' }}>{s.description}</Typography>
      </Box>
    </Stack>
  );

  const renderIdentity = () => (
    <Stack spacing={2.5}>
      {fieldBox('companyName', (
        <TextField
          required size="small" fullWidth autoFocus={!isEdit}
          label="Company Name"
          value={form.companyName}
          onChange={e => setField('companyName', e.target.value)}
          helperText="Legal or commonly-known name."
        />
      ))}
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          {fieldBox('website', (
            <TextField
              size="small" fullWidth
              label="Domain / Website"
              placeholder="https://acme.com"
              value={form.website}
              onChange={e => setField('website', e.target.value)}
            />
          ))}
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          {fieldBox('linkedInUrl', (
            <TextField
              size="small" fullWidth
              label="LinkedIn URL"
              placeholder="https://linkedin.com/company/..."
              value={form.linkedInUrl}
              onChange={e => setField('linkedInUrl', e.target.value)}
            />
          ))}
        </Grid>
      </Grid>

      {fieldBox('industryId', (
        <Autocomplete
          freeSolo
          options={industries}
          getOptionLabel={(o) => typeof o === 'string' ? o : (o?.name || '')}
          value={industries.find(i => i.id === form.industryId) || null}
          onChange={async (_, val) => {
            if (!val) { setField('industryId', ''); return; }
            if (typeof val === 'string') { await handleAddIndustry(val); return; }
            if (val.__add) { await handleAddIndustry(val.inputValue); return; }
            setField('industryId', val.id);
          }}
          filterOptions={(opts, params) => {
            const filtered = opts.filter(o => (o.name || '').toLowerCase().includes((params.inputValue || '').toLowerCase()));
            const exact = opts.some(o => (o.name || '').toLowerCase() === (params.inputValue || '').toLowerCase());
            if (params.inputValue && !exact) {
              filtered.push({ __add: true, inputValue: params.inputValue, name: `+ Add "${params.inputValue}"` });
            }
            return filtered;
          }}
          renderOption={(props, option) => (
            <li {...props} key={option.id || option.name}>
              {option.__add
                ? <Typography sx={{ color: '#4f46e5', fontWeight: 600 }}>{option.name}</Typography>
                : option.name}
            </li>
          )}
          renderInput={(params) => (
            <TextField {...params}
              label="Industry"
              size="small"
              helperText={addingIndustry ? 'Adding industry…' : 'Pick from existing or type to add new.'}
            />
          )}
        />
      ))}

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          {fieldBox('companyType', (
            <Autocomplete
              freeSolo
              options={COMPANY_TYPES}
              value={form.companyType}
              onChange={(_, v) => setField('companyType', v || '')}
              onInputChange={(_, v) => setField('companyType', v || '')}
              renderInput={(params) => (
                <TextField {...params} size="small" label="Company Type" placeholder="Private, Public, …" />
              )}
            />
          ))}
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          {fieldBox('headcount', (
            <TextField
              size="small" fullWidth type="number"
              label="Headcount"
              value={form.headcount}
              onChange={e => setField('headcount', e.target.value)}
              inputProps={{ min: 0 }}
            />
          ))}
        </Grid>
      </Grid>

      {fieldBox('description', (
        <TextField
          size="small" fullWidth multiline rows={4}
          label="Description"
          placeholder="What does the company do? Their products, market, positioning…"
          value={form.description}
          onChange={e => setField('description', e.target.value)}
          helperText="Drives ICP scoring, AI enrichment, and outreach personalization."
        />
      ))}
    </Stack>
  );

  const renderLocation = () => (
    <Stack spacing={2.5}>
      {fieldBox('address', (
        <TextField
          size="small" fullWidth
          label="Street Address"
          value={form.address}
          onChange={e => setField('address', e.target.value)}
        />
      ))}
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          {fieldBox('city', (<TextField size="small" fullWidth label="City" value={form.city} onChange={e => setField('city', e.target.value)} />))}
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          {fieldBox('state', (<TextField size="small" fullWidth label="State / Region" value={form.state} onChange={e => setField('state', e.target.value)} />))}
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          {fieldBox('postalCode', (<TextField size="small" fullWidth label="Postal Code" value={form.postalCode} onChange={e => setField('postalCode', e.target.value)} />))}
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 8 }}>
          {fieldBox('country', (<TextField size="small" fullWidth label="Country" value={form.country} onChange={e => setField('country', e.target.value)} />))}
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          {fieldBox('countryCode', (<TextField size="small" fullWidth label="Country Code" placeholder="US, GB, FR…" value={form.countryCode} onChange={e => setField('countryCode', e.target.value)} />))}
        </Grid>
      </Grid>
      <Divider />
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          {fieldBox('phone', (<TextField size="small" fullWidth label="Phone" value={form.phone} onChange={e => setField('phone', e.target.value)} />))}
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          {fieldBox('email', (<TextField size="small" fullWidth type="email" label="Email" value={form.email} onChange={e => setField('email', e.target.value)} />))}
        </Grid>
      </Grid>
    </Stack>
  );

  const renderClassification = () => (
    <Stack spacing={2.5}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          {fieldBox('source', (
            <TextField select size="small" fullWidth label="Source"
              value={form.source} onChange={e => setField('source', e.target.value)}>
              {SOURCES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
          ))}
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          {fieldBox('sourceDetail', (
            <TextField size="small" fullWidth label="Source Detail"
              placeholder="e.g. campaign URL, referrer name"
              value={form.sourceDetail} onChange={e => setField('sourceDetail', e.target.value)} />
          ))}
        </Grid>
      </Grid>
      {fieldBox('tags', (
        <TextField size="small" fullWidth label="Tags"
          placeholder="Comma-separated, e.g. enterprise, eu, q2-target"
          value={form.tags} onChange={e => setField('tags', e.target.value)} />
      ))}
      {isEdit && (
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            {fieldBox('status', (
              <TextField select size="small" fullWidth label="Status"
                value={form.status} onChange={e => setField('status', e.target.value)}>
                {STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </TextField>
            ))}
          </Grid>
        </Grid>
      )}
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          {fieldBox('assignedToUserId', (
            <TextField select size="small" fullWidth label="Assigned To"
              value={form.assignedToUserId} onChange={e => setField('assignedToUserId', e.target.value === '' ? '' : Number(e.target.value))}>
              <MenuItem value="">— Unassigned —</MenuItem>
              {users.map(u => <MenuItem key={u.id} value={u.id}>{u.fullName || u.email}</MenuItem>)}
            </TextField>
          ))}
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          {fieldBox('profilingTemplateId', (
            <TextField select size="small" fullWidth label="Profiling Template"
              value={form.profilingTemplateId} onChange={e => setField('profilingTemplateId', e.target.value === '' ? '' : Number(e.target.value))}>
              <MenuItem value="">— None —</MenuItem>
              {templates.map(t => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
            </TextField>
          ))}
        </Grid>
      </Grid>
      {fieldBox('notes', (
        <TextField size="small" fullWidth multiline rows={3} label="Internal Notes"
          placeholder="Private context — not shared with the lead"
          value={form.notes} onChange={e => setField('notes', e.target.value)} />
      ))}
    </Stack>
  );

  const renderSection = (s) => {
    let body = null;
    if (s.key === 'identity') body = renderIdentity();
    else if (s.key === 'location') body = renderLocation();
    else if (s.key === 'classification') body = renderClassification();
    return (
      <Paper
        key={s.key}
        elevation={0}
        ref={el => { if (el) sectionRefs.current[s.key] = el; }}
        sx={{ p: 3, borderRadius: 2, border: '1px solid #f1f5f9', scrollMarginTop: 16 }}
      >
        {sectionHeader(s)}
        {body}
      </Paper>
    );
  };

  if (loading) {
    return <Box sx={{ p: 5, textAlign: 'center' }}><CircularProgress /></Box>;
  }

  const completenessColor = completeness >= 80 ? '#16a34a' : completeness >= 50 ? '#d97706' : '#dc2626';
  const completenessChipBg = completeness >= 80 ? '#dcfce7' : completeness >= 50 ? '#fef9c3' : '#fee2e2';
  const completenessChipFg = completeness >= 80 ? '#166534' : completeness >= 50 ? '#854d0e' : '#991b1b';

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Button size="small" startIcon={<BackIcon />} onClick={() => navigate(isEdit ? `/leads/${id}` : '/leads')}
            sx={{ color: '#64748b', mb: 0.5 }}>
            {isEdit ? 'Back to lead' : 'Back to leads'}
          </Button>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#0f172a' }}>
            {isEdit ? `Edit ${form.companyName || 'lead'}` : 'Add new lead'}
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b', mt: 0.25 }}>
            Only Company Name is required. Richer profile drives better scoring & outreach.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Chip
            size="small"
            label={`${completeness}% complete`}
            sx={{ fontWeight: 700, bgcolor: completenessChipBg, color: completenessChipFg }}
          />
          <Button
            variant="text"
            color="inherit"
            disabled={saving}
            onClick={() => navigate(isEdit ? `/leads/${id}` : '/leads')}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={saving ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving…' : (isEdit ? 'Save Changes' : 'Create Lead')}
          </Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}

      <Grid container spacing={3}>
        {/* Left: guided config */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Stack spacing={3}>
            {SECTIONS.map(renderSection)}
          </Stack>
        </Grid>

        {/* Right: preview + suggestions */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Box sx={{ position: { md: 'sticky' }, top: { md: 16 } }}>
            <Stack spacing={2}>
              {/* Completeness */}
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: '#e2e8f0' }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                  <Typography sx={{ fontWeight: 700, color: '#0f172a' }}>Profile completeness</Typography>
                  <Chip
                    size="small"
                    label={`${completeness}%`}
                    sx={{ fontWeight: 700, bgcolor: completenessChipBg, color: completenessChipFg }}
                  />
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={completeness}
                  sx={{
                    height: 8, borderRadius: 4, bgcolor: '#f1f5f9',
                    '& .MuiLinearProgress-bar': { bgcolor: completenessColor },
                  }}
                />
              </Paper>

              {/* Suggestions */}
              {missingFields.length > 0 && (
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: '#fde68a', bgcolor: '#fffbeb' }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                    <SuggestIcon sx={{ color: '#d97706' }} />
                    <Typography sx={{ fontWeight: 700, color: '#92400e' }}>
                      Suggestions ({missingFields.length})
                    </Typography>
                  </Stack>
                  <Stack spacing={1} sx={{ maxHeight: 320, overflowY: 'auto', pr: 0.5 }}>
                    {missingFields.map(k => (
                      <Stack key={k} direction="row" alignItems="center" justifyContent="space-between"
                        sx={{ p: 1, borderRadius: 1, bgcolor: '#fff', border: '1px solid #fef3c7' }}>
                        <Box sx={{ minWidth: 0, flex: 1, mr: 1 }}>
                          <Typography sx={{ fontWeight: 600, fontSize: '0.8125rem' }}>{FIELD_LABELS[k] || k}</Typography>
                          {SUGGEST_HINTS[k] && (
                            <Typography sx={{ fontSize: '0.7rem', color: '#78716c' }}>{SUGGEST_HINTS[k]}</Typography>
                          )}
                        </Box>
                        <Button size="small" variant="text" startIcon={<EditIcon fontSize="small" />}
                          onClick={() => jumpToField(k)}>
                          Fill
                        </Button>
                      </Stack>
                    ))}
                  </Stack>
                </Paper>
              )}

              {/* Live preview / summary */}
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, borderColor: '#e2e8f0' }}>
                <Typography sx={{ fontWeight: 700, mb: 1.5, color: '#0f172a' }}>Preview</Typography>
                <Box sx={{ maxHeight: 480, overflowY: 'auto', pr: 0.5 }}>
                  <Grid container spacing={1.25}>
                    {Object.entries(FIELD_LABELS).map(([k, label]) => {
                      let v = form[k];
                      if (k === 'industryId') v = industries.find(i => i.id === form.industryId)?.name;
                      if (k === 'assignedToUserId') v = users.find(u => u.id === form.assignedToUserId)?.fullName;
                      if (k === 'profilingTemplateId') v = templates.find(t => t.id === form.profilingTemplateId)?.name;
                      return (
                        <Grid size={{ xs: 12 }} key={k}>
                          <Typography sx={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#94a3b8', fontWeight: 600 }}>{label}</Typography>
                          <Typography sx={{ fontSize: '0.8125rem', color: v ? '#0f172a' : '#cbd5e1', wordBreak: 'break-word' }}>
                            {v || '—'}
                          </Typography>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              </Paper>
            </Stack>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
